<script setup lang="ts">
import { reactive, computed } from 'vue';

// Component properties
const props = withDefaults(
    defineProps<{
        /** The HTML tag of the component (div, a) */
        tag?: string;
        /** The title of the block */
        title?: string | null;
        /** The subtitle of the block */
        subtitle?: string | null;
        /** Bordered block style */
        bordered?: boolean;
        /** Rounded block style */
        rounded?: boolean;
        /** Themed block style */
        themed?: boolean;
        /** Transparent block style */
        transparent?: boolean;
        /** Shadow fx block style */
        fxShadow?: boolean;
        /** Pop fx block style */
        fxPop?: boolean;
        /** Rotate right fx block style */
        fxRotateRight?: boolean;
        /** Rotate left fx block style */
        fxRotateLeft?: boolean;
        /** Shadow style for block links */
        linkShadow?: boolean;
        /** Pop style for block links */
        linkPop?: boolean;
        /** Rotate style for block links */
        linkRotate?: boolean;
        /** Add additional classes to default block-header */
        headerClass?: string | null;
        /** Add additional classes to default block-options */
        optionsClass?: string | null;
        /** Add additional classes to default block-content */
        contentClass?: string | null;
        /** Add additional classes to default block footer */
        footerClass?: string | null;
        /** Add the default header background */
        headerBg?: boolean;
        /** Reverse the order of elements in header */
        headerRtl?: boolean;
        /** Add full padding to the bottom of the default block-content */
        contentFull?: boolean;
        /** Remove all padding from block content */
        noPadding?: boolean;
        /** Enable the ribbon or enable it and also specify its content by setting a string value. */
        ribbon?: boolean | string;
        /** Position ribbon to the left */
        ribbonLeft?: boolean;
        /** Position ribbon to the bottom */
        ribbonBottom?: boolean;
        /** Set the bookmark type to your ribbon */
        ribbonBookmark?: boolean;
        /** Set the modern type to your ribbon */
        ribbonModern?: boolean;
        /** Set a different ribbon variant (primary, info, warning, danger, success, glass) */
        ribbonVariant?: string;
        /** Loading block mode */
        modeLoading?: boolean;
        /** Fullscreen block mode */
        modeFullscreen?: boolean;
        /** Pinned block mode */
        modePinned?: boolean;
        /** Hide block's content */
        modeContentHide?: boolean;
        /** Hide block itself */
        modeHide?: boolean;
        /** Enable the fullscreen mode button */
        btnOptionFullscreen?: boolean;
        /** Enable the pinned mode button */
        btnOptionPinned?: boolean;
        /** Enable the content toggle button */
        btnOptionContent?: boolean;
        /** Enable the close mode button */
        btnOptionClose?: boolean;
    }>(),
    {
        tag: 'div',
        title: null,
        subtitle: null,
        bordered: false,
        rounded: true,
        themed: false,
        transparent: false,
        fxShadow: false,
        fxPop: false,
        fxRotateRight: false,
        fxRotateLeft: false,
        linkShadow: false,
        linkPop: false,
        linkRotate: false,
        headerClass: null,
        optionsClass: null,
        contentClass: null,
        footerClass: null,
        headerBg: true,
        headerRtl: false,
        contentFull: false,
        noPadding: false,
        ribbon: false,
        ribbonLeft: false,
        ribbonBottom: false,
        ribbonBookmark: false,
        ribbonModern: false,
        ribbonVariant: 'primary',
        modeLoading: false,
        modeFullscreen: false,
        modePinned: false,
        modeContentHide: false,
        modeHide: false,
        btnOptionFullscreen: false,
        btnOptionPinned: false,
        btnOptionContent: false,
        btnOptionClose: false,
    },
);

// Reactive state
const state = reactive({
    // If the block is in loading mode
    optionLoading: props.modeLoading,

    // If the block is in fullscreen mode
    optionFullscreen: props.modeFullscreen,

    // If the block is in pinned mode
    optionPinned: props.modePinned,

    // If the default block-content is hidden
    optionContentHide: props.modeContentHide,

    // If block itself is hidden
    optionHide: props.modeHide,
});

// Set CSS classes accordingly
const classContainer = computed(() => {
    return {
        'block-bordered': props.bordered,
        'block-rounded': props.rounded,
        'block-themed': props.themed,
        'block-transparent': props.transparent,
        'block-fx-shadow': props.fxShadow,
        'block-fx-pop': props.fxPop,
        'block-fx-rotate-right': props.fxRotateRight,
        'block-fx-rotate-left': props.fxRotateLeft,
        'block-link-shadow': props.linkShadow,
        'block-link-pop': props.linkPop,
        'block-link-rotate': props.linkRotate,
        'block-mode-loading': state.optionLoading,
        'block-mode-fullscreen': state.optionFullscreen,
        'block-mode-pinned': state.optionPinned,
        'block-mode-hidden': state.optionContentHide,
        'd-none': state.optionHide,
    };
});

// Set CSS classes for block header
const classContainerHeader = computed(() => [
    {
        'block-header-default': props.headerBg,
        'block-header-rtl': props.headerRtl,
    },
    props.headerClass,
]);

// Set CSS classes for block header options
const classContainerOptions = computed(() => {
    return props.optionsClass ? { [props.optionsClass]: true } : {};
});

// Set CSS classes for block footer options
const classContainerFooter = computed(() => {
    return props.footerClass ? { [props.footerClass]: true } : {};
});

// Set CSS classes for block content
const classContainerContent = computed(() => [
    {
        'block-content-full': props.contentFull,
        'block-content-no-padding': props.noPadding,
        ribbon: props.ribbon,
        'ribbon-left': props.ribbon && props.ribbonLeft,
        'ribbon-bottom': props.ribbon && props.ribbonBottom,
        'ribbon-bookmark': props.ribbon && props.ribbonBookmark,
        'ribbon-modern': props.ribbon && props.ribbonModern,
        [`ribbon-${props.ribbonVariant}`]: props.ribbon && props.ribbonVariant,
    },
    props.contentClass,
]);
const emit = defineEmits(['fullscreen-toggled']);

// Block functions to call on demand
function fullscreenToggle() {
    state.optionPinned = false;
    state.optionFullscreen = !state.optionFullscreen;
    emit('fullscreen-toggled');
}

function fullscreenOn() {
    state.optionPinned = false;
    state.optionFullscreen = true;
}

function fullscreenOff() {
    state.optionPinned = false;
    state.optionFullscreen = false;
}

function pinnedToggle() {
    state.optionFullscreen = false;
    state.optionPinned = !state.optionPinned;
}

function pinnedOn() {
    state.optionFullscreen = false;
    state.optionPinned = true;
}

function pinnedOff() {
    state.optionFullscreen = false;
    state.optionPinned = false;
}

function contentToggle() {
    state.optionContentHide = !state.optionContentHide;
}

function contentShow() {
    state.optionContentHide = false;
}

function contentHide() {
    state.optionContentHide = true;
}

function statusToggle() {
    state.optionLoading = !state.optionLoading;
}

function statusLoading(mode: string, timeout: number) {
    state.optionLoading = true;

    if (mode === 'demo') {
        setTimeout(() => {
            state.optionLoading = false;
        }, timeout);
    }
}

function statusNormal() {
    state.optionLoading = false;
}

function open() {
    state.optionHide = false;
}

function close() {
    state.optionHide = true;
}

defineExpose({
    fullscreenToggle,
    fullscreenOn,
    fullscreenOff,
    pinnedToggle,
    pinnedOn,
    pinnedOff,
    contentToggle,
    contentShow,
    contentHide,
    statusToggle,
    statusLoading,
    statusNormal,
    open,
    close,
});
</script>

<template>
    <component :is="tag" :href="tag === 'a' ? '#' : null" :class="classContainer" class="block">
        <!-- Block Header -->
        <div v-if="$slots['header'] || $slots['title'] || title" class="block-header" :class="classContainerHeader">
            <slot name="header">
                <!-- Block Title -->
                <h3 class="block-title">
                    <slot name="title" />
                    {{ title }}
                    <small v-if="subtitle">{{ subtitle }}</small>
                    <slot name="subtitle" />
                </h3>
                <!-- END Block Title -->

                <!-- Block Options -->
                <div
                    v-if="
                        $slots['options'] ||
                        btnOptionFullscreen ||
                        btnOptionPinned ||
                        btnOptionContent ||
                        btnOptionClose
                    "
                    class="block-options space-x-1"
                    :class="classContainerOptions"
                >
                    <slot name="options" />
                    <button v-if="btnOptionFullscreen" type="button" class="btn-block-option" @click="fullscreenToggle">
                        <i
                            :class="{
                                'si si-size-fullscreen': !state.optionFullscreen,
                                'si si-size-actual': state.optionFullscreen,
                            }"
                        />
                    </button>
                    <button v-if="btnOptionPinned" type="button" class="btn-block-option" @click="pinnedToggle">
                        <i class="si si-pin" />
                    </button>
                    <button v-if="btnOptionContent" type="button" class="btn-block-option" @click="contentToggle">
                        <i
                            :class="{
                                'si si-arrow-up': !state.optionContentHide,
                                'si si-arrow-down': state.optionContentHide,
                            }"
                        />
                    </button>
                    <button v-if="btnOptionClose" type="button" class="btn-block-option" @click="close">
                        <i class="si si-close" />
                    </button>
                </div>
                <!-- END Block Options -->
            </slot>
        </div>
        <!-- END Block Header -->

        <!-- Default Block Content -->
        <div v-if="!$slots['content']" class="block-content" :class="classContainerContent">
            <div v-if="props.ribbon" class="ribbon-box">
                <slot name="ribbon">
                    {{ ribbon }}
                </slot>
            </div>
            <slot />
        </div>
        <!-- END Default Block Content -->

        <!-- Default Block Footer -->
        <div
            v-if="!$slots['content'] && $slots['footer']"
            class="block-content block-content-full block-content-sm bg-body-light fs-sm"
            :class="classContainerFooter"
        >
            <slot name="footer" />
        </div>
        <!-- END Default Block Footer -->

        <!-- Main content for full block content control -->
        <slot name="content" />
    </component>
</template>
